import React, { useState } from 'react';

// material
import { alpha, styled } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';

import {
  Card,
  Box,
  Typography,
  Modal,
  FormControl,
  TextField,
  Snackbar,
  Alert
} from '@mui/material';

import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import { LoadingButton } from '@mui/lab';

import { EndTripById } from '../../../network/Trip';

// ----------------------------------------------------------------------

const RootStyle = styled(Card)(({ theme }) => ({
  boxShadow: 'none',
  textAlign: 'center',
  padding: theme.spacing(5, 0),
  color: theme.palette.primary.darker,
  backgroundColor: '#FFFFFF'
}));

const IconWrapperStyle = styled('div')(({ theme }) => ({
  margin: 'auto',
  display: 'flex',
  borderRadius: '50%',
  alignItems: 'center',
  width: theme.spacing(8),
  height: theme.spacing(8),
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  color: theme.palette.primary.dark,
  backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0)} 0%, ${alpha(
    theme.palette.primary.dark,
    0.24
  )} 100%)`
}));

const useStyles = makeStyles({
  customDashCard: {
    background: '#FFFFFF',
    boxShadow: '10px 10px 19px 1px #EAEAFF',
    borderRadius: '20px',
    minHeight: '190px'
  }
});

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '60%', sm: '350px' },
  bgcolor: 'background.paper',
  p: 2,
  borderRadius: 1
};

const EndTripSchema = Yup.object().shape({
  comments: Yup.string().min(2, 'Too Short!').required('Required')
});

export default function EndTripModal({
  openAssignedModal,
  tripId,
  handleClose,
  onAction,
  alertMetas,
  setAlertMetas
}) {
  const endTripFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      comments: ''
    },
    validationSchema: EndTripSchema,
    onSubmit: (values, { resetForm }) => {
      const payload = {
        details: values.comments
      };
      EndTrip(payload, resetForm);
    }
  });

  const EndTrip = async (data, resetForm) => {
    try {
      const response = await EndTripById(tripId, data);
      if (response) {
        setAlertMetas({
          ...alertMetas,
          severity: 'success',
          snackBarOpened: true,
          msg: 'The trip has ended now.'
        });
        setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
      }
    } catch (error) {
      setAlertMetas({
        ...alertMetas,
        severity: 'error',
        snackBarOpened: true,
        msg: error?.response?.data?.message
      });
      setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
    } finally {
      endTripFormik.setSubmitting(false);
      handleClose();
      onAction();
      resetForm();
    }
  };

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = endTripFormik;

  return (
    <Box>
      <Modal
        open={openAssignedModal}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        data-testid="end-trip-modal"
      >
        <Box sx={style}>
          <Typography sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 900 }}>End Trip</Typography>

          <FormikProvider value={endTripFormik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Box sx={{ mt: { xs: 1, sm: 3 } }}>
                <div>
                  <FormControl
                    variant="outlined"
                    sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                    fullWidth
                  >
                    <TextField
                      label="Comments"
                      id="comments"
                      data-testid="comments"
                      fullWidth
                      {...getFieldProps('comments')}
                      error={Boolean(touched.comments && errors.comments)}
                      helperText={touched.comments && errors.comments}
                      multiline
                      rows={4}
                      sx={{
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '12px', sm: '14px' }
                        },
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '12px', sm: '14px' }
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: { xs: '12px', sm: '14px' }
                        }
                      }}
                    />
                  </FormControl>
                </div>
              </Box>

              <Box sx={{ textAlign: 'center', width: '100%', mt: { xs: 2, sm: 3 } }}>
                <LoadingButton
                  fullWidth
                  size="medium"
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                >
                  End
                </LoadingButton>
              </Box>
            </Form>
          </FormikProvider>
        </Box>
      </Modal>
    </Box>
  );
}
